import { SearchIcon, Settings, RotateCw, X, FilterX } from "lucide-react";
import React, { useState, useEffect, useCallback } from "react";
import Categoryheader from "./Components/VocuherCategory";
import ControlledAccordions from "./Components/Collapsable";
import { Permissions } from "../../../permissions";
import { IconButton } from "@mui/material";
import { errorToast, successToast } from "../../../services/toast.service";
import { enhancedVouchers, fetchVouchers } from "../../../dummyData";

const ViewVoucher = () => {
  const [selected, setselected] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    sortBy: "",
    status: "",
    discount: "",
  });
  const [voucherData, setVoucherData] = useState({
    common: [],
    userSpecific: [],
  });

  // Check permissions
  let common =
    Permissions.find(permission =>
      permission.includes("show_commonVouchers"),
    ) !== undefined;

  let user =
    Permissions.find(permission => permission.includes("show_userVouchers")) !==
    undefined;

  // Fetch vouchers based on filters and search
  const fetchVoucherData = useCallback(() => {
    const allFilters = {
      ...filters,
      searchQuery: debouncedSearch,
    };

    // Fetch vouchers using the dummy data function
    const result = fetchVouchers(allFilters);
    setVoucherData(result);
  }, [filters, debouncedSearch]);

  // Initial data load
  useEffect(() => {
    fetchVoucherData();
  }, [fetchVoucherData]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500); // Wait 500ms after last keystroke

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Simulate API call with a delay
      await new Promise(resolve => setTimeout(resolve, 800));
      fetchVoucherData();
      successToast("Voucher list refreshed");
    } catch (error) {
      errorToast("Failed to refresh voucher list");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleFilterChange = newFilters => {
    setFilters(newFilters);
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setFilters({
      sortBy: "",
      status: "",
      discount: "",
    });
  };

  // Get vouchers to display based on selected tab
  const getVouchersToDisplay = () => {
    if (selected === 0) {
      // All vouchers
      return [...voucherData.common, ...voucherData.userSpecific];
    } else if (selected === 1) {
      // Common vouchers
      return voucherData.common;
    } else {
      // User specific vouchers
      return voucherData.userSpecific;
    }
  };

  const displayVouchers = getVouchersToDisplay();

  return (
    <div className="bg-white h-[calc(100vh-120px)]">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <h1 className="text-2xl font-semibold font-outfit text-gray-800">
          View Vouchers
        </h1>

        {/* Search and Settings Section */}
        <div className="flex items-center gap-4">
          {/* Search Bar */}
          <div className="w-[550px]">
            <div className="relative">
              <SearchIcon
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                size={18}
                color="#83899F"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by Voucher Name, Discount, Validity..."
                className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-200 
                         focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] 
                         outline-none transition-all text-gray-600"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:bg-gray-100 p-1 rounded-full">
                  <X size={16} color="#83899F" />
                </button>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <IconButton
              onClick={handleRefresh}
              className="hover:bg-gray-100"
              disabled={isRefreshing}
              title="Refresh">
              <RotateCw
                size={20}
                className={`text-gray-600 ${
                  isRefreshing ? "animate-spin" : ""
                }`}
              />
            </IconButton>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Filter Section */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selected === 0
                  ? "bg-[#EAD8FC] text-[#5652B0] border-2 border-[#5652B0]"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
              }`}
              onClick={() => setselected(0)}>
              All
            </button>
            {common && (
              <button
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  selected === 1
                    ? "bg-[#EAD8FC] text-[#5652B0] border-2 border-[#5652B0]"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                }`}
                onClick={() => setselected(1)}>
                Common
              </button>
            )}
            {user && (
              <button
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  selected === 2
                    ? "bg-[#EAD8FC] text-[#5652B0] border-2 border-[#5652B0]"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                }`}
                onClick={() => setselected(2)}>
                User Specific
              </button>
            )}
          </div>

          <div className="flex items-center gap-4">
            <Categoryheader
              onFilterChange={handleFilterChange}
              filters={filters}
            />
            {(searchQuery ||
              Object.values(filters).some(value => value !== "")) && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 
                         hover:bg-gray-100 rounded-lg transition-colors">
                <FilterX size={16} />
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Vouchers List */}
        <div className="space-y-4">
          {displayVouchers.length > 0 ? (
            displayVouchers.map(voucher => (
              <ControlledAccordions
                key={voucher.id}
                isUserSpecific={
                  selected === 2 || (selected === 0 && voucher.userId)
                }
                voucherData={voucher}
              />
            ))
          ) : (
            <div className="py-10 text-center">
              <p className="text-gray-500 text-lg">
                No vouchers found matching your criteria
              </p>
              <button
                onClick={clearAllFilters}
                className="mt-4 px-4 py-2 text-sm font-medium text-[#5652B0] border border-[#5652B0] rounded-lg hover:bg-[#F9F8FF]">
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewVoucher;
